import { useState, useEffect } from 'react';
import Select from 'react-select';
import Drawer from '~/components/Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { apiUrl, authHeaders } from '~/services/auth';
import toast from 'react-hot-toast';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    campaignRequest: any;
    campaign: any;
    patient?: any;
    metadata?: any;
    onUpdate?: () => void;
}

const MedicalReasonDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    campaignRequest,
    campaign,
    patient,
    metadata,
    onUpdate,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedMedicalReason, setSelectedMedicalReason] =
        useState<any>(null);
    const [selectedPrimaryMedicalIssue, setSelectedPrimaryMedicalIssue] =
        useState<any>(null);
    const [selectedInjuryTimeFrame, setSelectedInjuryTimeFrame] =
        useState<any>(null);
    const [selectedTransplantDiagnosis, setSelectedTransplantDiagnosis] =
        useState<any>(null);
    const [selectedCandidacyStatus, setSelectedCandidacyStatus] =
        useState<any>(null);
    const [listingDate, setListingDate] = useState('');
    const [livingDonor, setLivingDonor] = useState(false);
    const [medicalIssueDate, setMedicalIssueDate] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedSecondaryMedicalReason, setSelectedSecondaryMedicalReason] =
        useState<any>(null);
    const [selectedSecondaryMedicalIssue, setSelectedSecondaryMedicalIssue] =
        useState<any>(null);

    // Resolve the full medical reason record (with code) for conditional rendering
    const primaryMedicalReasonRecord = selectedMedicalReason
        ? (metadata?.medical_reasons || []).find(
              (mr: any) => mr.id === selectedMedicalReason.value,
          )
        : null;
    const primaryMedicalReasonCode = primaryMedicalReasonRecord?.code || '';
    const isTransplant = primaryMedicalReasonCode === 'transplant';
    const dateLabel =
        primaryMedicalReasonCode === 'transplant'
            ? 'Transplant Date'
            : primaryMedicalReasonCode === 'cat_injury'
            ? 'Accident Date'
            : primaryMedicalReasonCode === 'cat_illness'
            ? 'Illness Date'
            : 'Medical Issue Date';
    const candidacyCode = selectedCandidacyStatus
        ? (metadata?.candidacy_statuses || []).find(
              (cs: any) => cs.id === selectedCandidacyStatus.value,
          )?.code
        : null;
    const showListingDate = isTransplant && candidacyCode === 'listed';

    // Prepare options for selects
    const medicalReasonOptions = (metadata?.medical_reasons || []).map(
        (mr: any) => ({
            value: mr.id,
            label: mr.name,
        }),
    );

    // Filter medical issues based on selected medical reason
    const filteredMedicalIssues = selectedMedicalReason
        ? (metadata?.medical_issues || []).filter((mi: any) => {
              const medicalReasonId =
                  typeof mi.medical_reason === 'object'
                      ? mi.medical_reason?.id
                      : mi.medical_reason;
              return medicalReasonId === selectedMedicalReason.value;
          })
        : metadata?.medical_issues || [];

    const medicalIssueOptions = filteredMedicalIssues.map((mi: any) => ({
        value: mi.id,
        label: mi.name,
    }));

    // Filter secondary medical issues based on selected secondary medical reason
    const filteredSecondaryMedicalIssues = selectedSecondaryMedicalReason
        ? (metadata?.medical_issues || []).filter((mi: any) => {
              const medicalReasonId =
                  typeof mi.medical_reason === 'object'
                      ? mi.medical_reason?.id
                      : mi.medical_reason;
              return medicalReasonId === selectedSecondaryMedicalReason.value;
          })
        : metadata?.medical_issues || [];

    const secondaryMedicalIssueOptions = filteredSecondaryMedicalIssues.map(
        (mi: any) => ({
            value: mi.id,
            label: mi.name,
        }),
    );

    const filteredInjuryTimeFrames = selectedMedicalReason
        ? (metadata?.injury_time_frames || []).filter((itf: any) => {
              const medicalReasonId =
                  typeof itf.medical_reason === 'object'
                      ? itf.medical_reason?.id
                      : itf.medical_reason;
              return medicalReasonId === selectedMedicalReason.value;
          })
        : metadata?.injury_time_frames || [];

    const injuryTimeFrameOptions = filteredInjuryTimeFrames.map((itf: any) => ({
        value: itf.id,
        label: itf.name,
    }));

    const transplantDiagnosisOptions = (
        metadata?.transplant_diagnoses || []
    ).map((td: any) => ({
        value: td.id,
        label: td.name,
    }));

    const candidacyStatusOptions = (metadata?.candidacy_statuses || []).map(
        (cs: any) => ({
            value: cs.id,
            label: cs.name,
        }),
    );

    // Helper to resolve a medical reason (could be object or ID)
    const resolveMedicalReason = (medicalReason: any) => {
        if (!medicalReason) return null;
        if (typeof medicalReason === 'number') {
            return (
                metadata?.medical_reasons?.find(
                    (mr: any) => mr.id === medicalReason,
                ) || null
            );
        }
        return medicalReason;
    };

    // Initialize form with current values when drawer opens
    useEffect(() => {
        if (isOpen) {
            const patientData = patient || campaign?.patient;

            // Primary medical issue & reason
            const primaryMedicalIssue = patientData?.primary_medical_issue;
            const medicalReason = resolveMedicalReason(
                primaryMedicalIssue?.medical_reason,
            );

            setSelectedMedicalReason(
                medicalReason
                    ? { value: medicalReason.id, label: medicalReason.name }
                    : null,
            );
            setSelectedPrimaryMedicalIssue(
                primaryMedicalIssue
                    ? {
                          value: primaryMedicalIssue.id,
                          label: primaryMedicalIssue.name,
                      }
                    : null,
            );

            // Injury Time Frame
            const injuryTimeFrame = patientData?.injury_time_frame;
            setSelectedInjuryTimeFrame(
                injuryTimeFrame
                    ? { value: injuryTimeFrame.id, label: injuryTimeFrame.name }
                    : null,
            );

            // Transplant Diagnosis
            const transplantDiagnosis = patientData?.transplant_diagnosis;
            setSelectedTransplantDiagnosis(
                transplantDiagnosis
                    ? {
                          value: transplantDiagnosis.id,
                          label: transplantDiagnosis.name,
                      }
                    : null,
            );

            // Candidacy Status (transplant only)
            const candidacyStatus = patientData?.candidacy_status;
            setSelectedCandidacyStatus(
                candidacyStatus
                    ? { value: candidacyStatus.id, label: candidacyStatus.name }
                    : null,
            );

            // Listing Date (transplant only, when candidacy is 'listed')
            setListingDate(patientData?.listing_date || '');

            // Living Donor (transplant only)
            setLivingDonor(Boolean(patientData?.living_donor));

            // Medical Issue Date
            setMedicalIssueDate(patientData?.medical_issue_date || '');

            // Notes (inherited from Role)
            setNotes(patientData?.notes || '');

            // Secondary medical issue & reason
            const secondaryMedicalIssue = patientData?.secondary_medical_issue;
            const secondaryMedicalReason = resolveMedicalReason(
                secondaryMedicalIssue?.medical_reason,
            );

            setSelectedSecondaryMedicalReason(
                secondaryMedicalReason
                    ? {
                          value: secondaryMedicalReason.id,
                          label: secondaryMedicalReason.name,
                      }
                    : null,
            );
            setSelectedSecondaryMedicalIssue(
                secondaryMedicalIssue
                    ? {
                          value: secondaryMedicalIssue.id,
                          label: secondaryMedicalIssue.name,
                      }
                    : null,
            );
        }
    }, [isOpen, campaign, patient, metadata]);

    // Auto-derive medical reason from selected medical issue
    const deriveMedicalReason = (selectedIssue: any) => {
        if (!selectedIssue || !metadata?.medical_issues) return null;
        const medicalIssue = metadata.medical_issues.find(
            (mi: any) => mi.id === selectedIssue.value,
        );
        if (!medicalIssue?.medical_reason) return null;
        const mrData =
            typeof medicalIssue.medical_reason === 'object'
                ? medicalIssue.medical_reason
                : metadata.medical_reasons?.find(
                      (mr: any) => mr.id === medicalIssue.medical_reason,
                  );
        return mrData ? { value: mrData.id, label: mrData.name } : null;
    };

    useEffect(() => {
        const derived = deriveMedicalReason(selectedPrimaryMedicalIssue);
        if (derived) setSelectedMedicalReason(derived);
    }, [selectedPrimaryMedicalIssue, metadata]);

    useEffect(() => {
        const derived = deriveMedicalReason(selectedSecondaryMedicalIssue);
        if (derived) setSelectedSecondaryMedicalReason(derived);
    }, [selectedSecondaryMedicalIssue, metadata]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required field
        if (!selectedPrimaryMedicalIssue) {
            toast.error('Primary medical issue is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const headers = authHeaders();
            const url = `${apiUrl}/admin/campaigns/${campaignRequest.campaign.id}/medicalInfo`;

            const resp = await fetch(url, {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                    primary_medical_issue_id:
                        selectedPrimaryMedicalIssue?.value || null,
                    injury_time_frame_id:
                        selectedInjuryTimeFrame?.value || null,
                    transplant_diagnosis_id: isTransplant
                        ? selectedTransplantDiagnosis?.value || null
                        : null,
                    candidacy_status_id: isTransplant
                        ? selectedCandidacyStatus?.value || null
                        : null,
                    listing_date: showListingDate ? listingDate || null : null,
                    living_donor: isTransplant ? livingDonor : false,
                    medical_issue_date: medicalIssueDate || null,
                    notes,
                    secondary_medical_issue_id:
                        selectedSecondaryMedicalIssue?.value || null,
                }),
            });

            if (!resp.ok) {
                const errorData = await resp.json().catch(() => ({}));
                throw new Error(
                    errorData.error ||
                        errorData.detail ||
                        'Failed to update medical information',
                );
            }

            toast.success('Medical information updated successfully');

            if (onUpdate) {
                await onUpdate();
            }

            setIsOpen(false);
        } catch (error: any) {
            console.error('Error updating medical information:', error);
            toast.error(
                error.message || 'Failed to update medical information',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Edit Medical Info'>
            <form onSubmit={handleSubmit}>
                <h3 className='mb-2 text-[14px] font-bold uppercase text-[#949599]'>
                    Primary Medical Reason
                </h3>

                <div className='mb-6'>
                    <label className={labelStyles}>Medical Reason</label>
                    <Select
                        menuPlacement='auto'
                        value={selectedMedicalReason}
                        onChange={(value) => {
                            setSelectedMedicalReason(value);
                            setSelectedPrimaryMedicalIssue(null);
                            setSelectedInjuryTimeFrame(null);
                        }}
                        options={medicalReasonOptions}
                        isClearable
                        placeholder='Select Medical Reason'
                        className='react-select-container'
                        classNamePrefix='react-select'
                    />
                </div>

                <div className='mb-6'>
                    <label className={labelStyles}>
                        Primary Medical Issue
                        <span className={requiredStyles}>*</span>
                    </label>
                    <Select
                        menuPlacement='auto'
                        value={selectedPrimaryMedicalIssue}
                        onChange={setSelectedPrimaryMedicalIssue}
                        options={medicalIssueOptions}
                        isClearable
                        isDisabled={!selectedMedicalReason}
                        placeholder={
                            selectedMedicalReason
                                ? 'Select Primary Medical Issue'
                                : 'Select Medical Reason first'
                        }
                        className='react-select-container'
                        classNamePrefix='react-select'
                    />
                </div>

                {isTransplant && (
                    <div className='mb-6'>
                        <label className={labelStyles}>Diagnosis</label>
                        <Select
                            menuPlacement='auto'
                            value={selectedTransplantDiagnosis}
                            onChange={setSelectedTransplantDiagnosis}
                            options={transplantDiagnosisOptions}
                            isClearable
                            placeholder='Select Diagnosis'
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                    </div>
                )}

                <div className='mb-6'>
                    <label className={labelStyles}>Timeframe</label>
                    <Select
                        menuPlacement='auto'
                        value={selectedInjuryTimeFrame}
                        onChange={setSelectedInjuryTimeFrame}
                        options={injuryTimeFrameOptions}
                        isClearable
                        placeholder='Select Timeframe'
                        className='react-select-container'
                        classNamePrefix='react-select'
                    />
                </div>

                <div className='mb-6'>
                    <label className={labelStyles}>{dateLabel}</label>
                    <input
                        type='date'
                        value={medicalIssueDate}
                        onChange={(e) => setMedicalIssueDate(e.target.value)}
                        className={inputStyles + ' w-full'}
                    />
                </div>

                {isTransplant && (
                    <>
                        <div className='mb-6'>
                            <label className={labelStyles}>
                                Candidacy Status
                            </label>
                            <Select
                                menuPlacement='auto'
                                value={selectedCandidacyStatus}
                                onChange={setSelectedCandidacyStatus}
                                options={candidacyStatusOptions}
                                isClearable
                                placeholder='Select Candidacy Status'
                                className='react-select-container'
                                classNamePrefix='react-select'
                            />
                        </div>

                        {showListingDate && (
                            <div className='mb-6'>
                                <label className={labelStyles}>
                                    Listing Date
                                </label>
                                <input
                                    type='date'
                                    value={listingDate}
                                    onChange={(e) =>
                                        setListingDate(e.target.value)
                                    }
                                    className={inputStyles + ' w-full'}
                                />
                            </div>
                        )}

                        <div className='mb-6'>
                            <label className='flex items-center gap-2 text-[14px] font-bold'>
                                <input
                                    type='checkbox'
                                    checked={livingDonor}
                                    onChange={(e) =>
                                        setLivingDonor(e.target.checked)
                                    }
                                />
                                Living Donor
                            </label>
                        </div>
                    </>
                )}

                <div className='mb-6'>
                    <label className={labelStyles}>Notes</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        className={inputStyles + ' w-full'}
                        placeholder='Text'
                    />
                </div>

                <hr className='my-6' />

                <h3 className='mb-2 text-[14px] font-bold uppercase text-[#949599]'>
                    Secondary Medical Reason
                </h3>

                <div className='mb-6'>
                    <label className={labelStyles}>Medical Reason</label>
                    <Select
                        menuPlacement='auto'
                        value={selectedSecondaryMedicalReason}
                        onChange={(value) => {
                            setSelectedSecondaryMedicalReason(value);
                            setSelectedSecondaryMedicalIssue(null);
                        }}
                        options={medicalReasonOptions}
                        isClearable
                        placeholder='Select Medical Reason'
                        className='react-select-container'
                        classNamePrefix='react-select'
                    />
                </div>

                <div className='mb-6'>
                    <label className={labelStyles}>
                        Secondary Medical Issue
                    </label>
                    <Select
                        menuPlacement='auto'
                        value={selectedSecondaryMedicalIssue}
                        onChange={setSelectedSecondaryMedicalIssue}
                        options={secondaryMedicalIssueOptions}
                        isClearable
                        isDisabled={!selectedSecondaryMedicalReason}
                        placeholder={
                            selectedSecondaryMedicalReason
                                ? 'Select Secondary Medical Issue'
                                : 'Select Medical Reason first'
                        }
                        className='react-select-container'
                        classNamePrefix='react-select'
                    />
                </div>

                <div className='mb-[30px] flex gap-[16px]'>
                    <button
                        type='button'
                        className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:opacity-50'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default MedicalReasonDrawer;
